import React from 'react';

// Fields that represent system events, not program data changes.
// These are excluded from the diff table.
const SYSTEM_FIELDS = new Set(['createdOn', 'statusId', 'comment', 'Merged Program Id']);

// Parse a comma-separated location string into an array of trimmed lines.
// Input:  "Main Campus: Active (NEW),Regional Center: Suspended,"
// Output: ["Main Campus: Active (NEW)", "Regional Center: Suspended"]
const parseLocationString = raw => {
    if (!raw) return [];
    return raw
        .replace(/,\s*$/, '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
};

const buildDiffRows = requestLogs => {
    if (!requestLogs?.length) return [];

    const fieldEntries = requestLogs.filter(log => !SYSTEM_FIELDS.has(log.fieldName));

    const groups = {};
    for (const entry of fieldEntries) {
        if (!groups[entry.fieldName]) groups[entry.fieldName] = [];
        groups[entry.fieldName].push(entry);
    }

    return Object.entries(groups).map(([fieldName, entries]) => {
        const sorted = [...entries].sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp));
        const earliest = sorted[0];
        const latest = sorted[sorted.length - 1];

        if (fieldName === 'Locations') {
            return {
                fieldName,
                isLocations: true,
                current: parseLocationString(earliest?.oldFieldValue),
                requested: parseLocationString(latest?.newFieldValue),
            };
        }

        return {
            fieldName,
            isLocations: false,
            current: earliest?.oldFieldValue ?? '',
            requested: latest?.newFieldValue ?? '',
        };
    });
};

const CellValue = ({ value, isLocations }) => {
    if (isLocations) {
        if (!value?.length) return <span className="text-muted">—</span>;
        return (
            <ul className="list-unstyled mb-0">
                {value.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
        );
    }
    return value ? <>{value}</> : <span className="text-muted">—</span>;
};

const Field = ({ label, children }) => (
    <div className="mb-3">
        <div className="text-muted small font-weight-bold">{label}</div>
        <div>{children}</div>
    </div>
);

export const ChangeDetailsRequestView = ({ requestDetails, requestLogs }) => {
    const diffRows = buildDiffRows(requestLogs);

    // Use the program's pre-request name from the log (oldFieldValue of the earliest Program
    // Name entry) when the request is changing the name; otherwise fall back to the request object.
    const nameEntries = requestLogs?.filter(l => l.fieldName === 'Program Name') ?? [];
    const earliestNameEntry = nameEntries.sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp))[0];
    const headerName = earliestNameEntry?.oldFieldValue || requestDetails.programName;

    return (
        <div className="request-type-details">
            <h5 className="mb-1">Change Details Request</h5>
            <hr className="mt-2 mb-3" />

            <Field label="Program">
                <div className="border rounded p-2 bg-light d-inline-block">
                    <strong>{headerName}</strong>
                    {requestDetails.programID
                        ? <span className="text-muted ml-2">(ID: {requestDetails.programID})</span>
                        : null}
                    {requestDetails.programStatus
                        ? <span className="text-muted ml-3">Status: {requestDetails.programStatus}</span>
                        : null}
                </div>
            </Field>

            <div className="mb-3">
                <div className="text-muted small font-weight-bold mb-2">Requested Changes</div>
                {diffRows.length > 0 ? (
                    <table className="table table-sm table-bordered mb-0">
                        <thead className="thead-light">
                            <tr>
                                <th style={{ width: '28%' }}>Field</th>
                                <th>Current</th>
                                <th>Requested</th>
                            </tr>
                        </thead>
                        <tbody>
                            {diffRows.map(row => (
                                <tr key={row.fieldName}>
                                    <td className="font-weight-bold align-top">{row.fieldName}</td>
                                    <td className="align-top">
                                        <CellValue value={row.current} isLocations={row.isLocations} />
                                    </td>
                                    <td className="align-top">
                                        <CellValue value={row.requested} isLocations={row.isLocations} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="text-muted font-italic small mb-0">No field changes found in request log.</p>
                )}
            </div>

            {requestDetails.additionalComments && (
                <Field label="Comments">
                    {requestDetails.additionalComments}
                </Field>
            )}
        </div>
    );
};
